/**
 * Historial y correcciones (§4.2). Lógica compartida.
 *
 * §4.2 arranca diciendo que equivocarse es el caso normal. Las tres correcciones que pide están
 * acá y ninguna borra el registro: editar peso y reps, reasignar la serie a otro ejercicio, y
 * mover una sesión completa de fecha.
 *
 * Mover una sesión recalcula el calendario y devuelve las frases del cambio: §4.2 pide que la
 * corrección diga qué le hizo al plan, y una hoja que promete "te muestra qué cambió" y después
 * se cierra en silencio es peor que no prometer nada.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  calcularCalendario,
  explicarCambios,
  formatearDuracion,
  metricasCardio,
  volumen,
  type CambioCalendario,
  type Ejercicio,
  type EntradaMotor,
  type Fecha,
  type MotivoOmision,
  type RegistroCardio,
  type Serie,
  type Sesion,
} from '@mg/core';

import { editarSerieLocal, moverSesionAFecha, reasignarSerieLocal } from './acciones.ts';
import { useDatos } from './useDatos.ts';

export interface EjercicioDeSesion {
  ejercicio: Ejercicio;
  series: Serie[];
  completadas: Serie[];
  omitida: Serie | null;
  volumenKg: number;
}

export interface SesionDetallada {
  sesion: Sesion;
  nombreDia: string | null;
  ejercicios: EjercicioDeSesion[];
  cardio: RegistroCardio | null;
  volumenKg: number;
  seriesHechas: number;
  /** true si alguna serie de la sesión anotó RIR. Sin ninguna, la columna sobra. */
  tieneRir: boolean;
}

/**
 * Series que quedaron esperando en una sesión futura. No son historial —todavía no pasaron— pero
 * son la consecuencia visible de un omitido, y §4.3 pide poder rastrearlas hasta su origen.
 */
export interface ArrastradaPendiente {
  ejercicio: Ejercicio;
  series: number;
  /** Fecha de la sesión de la que vienen. */
  desde: Fecha | null;
  /** Fecha de la sesión que las está esperando. */
  hacia: Fecha;
}

export interface ResultadoMover {
  sesionId: string;
  fecha: Fecha;
  cambios: CambioCalendario[];
}

/**
 * Los motivos se guardan en snake_case porque así viajan a la planilla. `.insignia` los pinta en
 * mayúsculas, y "EQUIPO_OCUPADO" no es algo que nadie diga en voz alta.
 */
export const NOMBRE_MOTIVO: Record<MotivoOmision, string> = {
  equipo_ocupado: 'Equipo ocupado',
  dolor: 'Me dolió',
  sin_tiempo: 'Sin tiempo',
  decision: 'A propósito',
};

export function nombreMotivo(motivo: MotivoOmision | null): string {
  return motivo === null ? '' : NOMBRE_MOTIVO[motivo];
}

export function useHistorial() {
  const datos = useDatos();
  const [abierta, setAbierta] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [movida, setMovida] = useState<ResultadoMover | null>(null);

  const sesiones = useMemo<SesionDetallada[]>(() => {
    return datos.sesiones
      // Las planificadas no son historial: son futuro. Listadas acá se leían como entrenamientos
      // fallados ("0 series · 0 t") cuando todavía no llegó el día. Viven en Plan.
      .filter((s) => s.estado !== 'planificada')
      .map((sesion) => {
        const suyas = datos.series.filter((x) => x.sesion_id === sesion.id);
        const ids = [...new Set(suyas.map((x) => x.ejercicio_id))];

        const ejercicios = ids
          .map((id) => {
            const ejercicio = datos.ejerciciosPorId.get(id);
            if (!ejercicio) return null;
            const series = suyas
              .filter((x) => x.ejercicio_id === id)
              .sort((a, b) => a.numero_serie - b.numero_serie);
            const completadas = series.filter((x) => x.completada);
            return {
              ejercicio,
              series,
              completadas,
              omitida: series.find((x) => !x.completada && x.motivo_omision !== null) ?? null,
              volumenKg: volumen(
                completadas.map((serie) => ({ serie, ejercicio, fecha: sesion.fecha, sesion_id: sesion.id })),
                'kg',
              ),
            } satisfies EjercicioDeSesion;
          })
          .filter((x): x is EjercicioDeSesion => x !== null);

        return {
          sesion,
          nombreDia: datos.rutinaDias.find((d) => d.id === sesion.rutina_dia_id)?.nombre ?? null,
          ejercicios,
          cardio: datos.cardio.find((c) => c.sesion_id === sesion.id) ?? null,
          volumenKg: ejercicios.reduce((a, e) => a + e.volumenKg, 0),
          seriesHechas: ejercicios.reduce((a, e) => a + e.completadas.length, 0),
          tieneRir: suyas.some((x) => x.rir !== null),
        };
      });
  }, [datos]);

  /** Lo que un omitido dejó pendiente y todavía espera en una sesión futura (§4.3). */
  const arrastradas = useMemo<ArrastradaPendiente[]>(() => {
    const fechaPorSesion = new Map(datos.sesiones.map((s) => [s.id, s.fecha]));
    const acumulado = new Map<string, ArrastradaPendiente>();

    for (const serie of datos.series) {
      if (serie.completada || serie.motivo_omision !== null) continue;
      if (serie.arrastrada_de_sesion_id === null) continue;
      const hacia = fechaPorSesion.get(serie.sesion_id);
      const ejercicio = datos.ejerciciosPorId.get(serie.ejercicio_id);
      if (!hacia || !ejercicio) continue;

      const clave = `${serie.sesion_id}:${serie.ejercicio_id}`;
      const previo = acumulado.get(clave);
      if (previo) {
        previo.series += 1;
        continue;
      }
      acumulado.set(clave, {
        ejercicio,
        series: 1,
        desde: fechaPorSesion.get(serie.arrastrada_de_sesion_id) ?? null,
        hacia,
      });
    }

    return [...acumulado.values()].sort((a, b) => (a.hacia < b.hacia ? -1 : 1));
  }, [datos]);

  /** La entrada del motor de calendario, armada igual que en useCalendario. */
  const entradaMotor = useMemo<EntradaMotor | null>(() => {
    if (!datos.planActivo || !datos.gimnasioActivo) return null;
    return {
      plan: datos.planActivo,
      gimnasio: datos.gimnasioActivo,
      diasBloqueados: datos.diasBloqueados,
      rutinaDias: datos.rutinaDias,
      ejerciciosPorDia: datos.ejerciciosPorDia,
      sesiones: datos.sesiones,
      hoy: datos.hoy,
    };
  }, [datos]);

  const editar = useCallback(
    async (serie: Serie, cambios: { peso?: number; reps?: number }): Promise<void> => {
      setError(null);
      try {
        await editarSerieLocal(serie, cambios);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se pudo editar.');
      }
    },
    [],
  );

  /** §4.2 — el peso estaba bien, estaba en el ejercicio equivocado. No se borra: se reasigna. */
  const reasignar = useCallback(
    async (serie: Serie, nuevoEjercicioId: string): Promise<void> => {
      setError(null);
      try {
        const maquina = datos.maquinaDe(nuevoEjercicioId);
        await reasignarSerieLocal(serie, nuevoEjercicioId, maquina?.id ?? null);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se pudo reasignar.');
      }
    },
    [datos],
  );

  /**
   * Mueve la sesión y explica qué le hizo al plan. El calendario se calcula dos veces —con la
   * fecha vieja y con la nueva— porque `explicarCambios` compara dos calendarios, no dos fechas.
   */
  const mover = useCallback(
    async (sesion: Sesion, fecha: Fecha): Promise<void> => {
      setError(null);
      try {
        const antes = entradaMotor ? calcularCalendario(entradaMotor) : null;
        await moverSesionAFecha(sesion, fecha);

        const cambios =
          entradaMotor && antes
            ? explicarCambios(
                antes,
                calcularCalendario({
                  ...entradaMotor,
                  sesiones: entradaMotor.sesiones.map((s) => (s.id === sesion.id ? { ...s, fecha } : s)),
                }),
              )
            : [];

        setMovida({ sesionId: sesion.id, fecha, cambios });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se pudo mover la sesión.');
      }
    },
    [entradaMotor],
  );

  return {
    datos,
    sesiones,
    arrastradas,
    abierta,
    setAbierta,
    detalle: sesiones.find((s) => s.sesion.id === abierta) ?? null,
    editar,
    reasignar,
    mover,
    movida,
    limpiarMovida: () => setMovida(null),
    error,
    formatearDuracion,
    metricasCardio,
    nombreMotivo,
  };
}
